import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchProject, fetchProjectTasks } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'

const STATUS_COLORS: Record<string, string> = {
  pending: '#6b7280', in_progress: '#3b82f6', completed: '#3fb950',
  blocked: '#f85149', deferred: '#8b5cf6', review: '#d29922',
}

const STATUS_ACTIONS: Record<string, { label: string; next: string }[]> = {
  pending: [{ label: 'Start', next: 'in_progress' }],
  in_progress: [{ label: 'Complete', next: 'completed' }, { label: 'Block', next: 'blocked' }],
  blocked: [{ label: 'Unblock', next: 'pending' }],
  completed: [{ label: 'Reopen', next: 'pending' }],
  deferred: [{ label: 'Resume', next: 'pending' }],
  review: [{ label: 'Approve', next: 'completed' }, { label: 'Reject', next: 'in_progress' }],
}

async function patchTask(projectId: string, taskId: string, patch: Record<string, string>) {
  const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1] || ''
  const res = await fetch(`/api/projects/${projectId}/tasks/${taskId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(patch),
  })
  if (!res.ok) throw new Error('Failed')
  return res.json()
}

export default function TaskDetailPage() {
  const { id: projectId, taskId } = useParams<{ id: string; taskId: string }>()
  const queryClient = useQueryClient()
  const [noteInput, setNoteInput] = useState('')

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProject(projectId!),
    enabled: !!projectId,
  })

  const { data: tasksData } = useQuery({
    queryKey: ['projectTasks', projectId],
    queryFn: () => fetchProjectTasks(projectId!),
    enabled: !!projectId,
  })

  const task = tasksData?.tasks?.find(t => t.id === taskId)

  const statusMut = useMutation({
    mutationFn: (status: string) => patchTask(projectId!, taskId!, { status, actor: 'joel' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projectTasks', projectId] }),
  })

  const noteMut = useMutation({
    mutationFn: (notes: string) => patchTask(projectId!, taskId!, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectTasks', projectId] })
      setNoteInput('')
    },
  })

  function submitNote() {
    if (!noteInput.trim()) return
    const ts = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    const entry = `**Joel** · ${ts}\n${noteInput.trim()}`
    const combined = task?.notes ? `${task.notes}\n\n---\n\n${entry}` : entry
    noteMut.mutate(combined)
  }

  if (!task) {
    return <div className="p-6 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
  }

  const actions = STATUS_ACTIONS[task.status] || []
  const deps = tasksData?.tasks?.filter(t => task.depends_on?.includes(t.id)) || []
  const blockers = tasksData?.tasks?.filter(t => task.blocks?.includes(t.id)) || []

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <Link to={`/project/${projectId}`} className="text-xs mb-3 inline-block no-underline" style={{ color: 'var(--text-muted)' }}>
        ← {project?.title || 'Project'}
      </Link>

      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-3 h-3 rounded-full mt-1.5 shrink-0"
          style={{ background: STATUS_COLORS[task.status] || '#6b7280' }}
        />
        <div className="flex-1">
          <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{task.title}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs capitalize px-2 py-0.5 rounded-full" style={{
              color: STATUS_COLORS[task.status],
              background: `${STATUS_COLORS[task.status]}20`,
            }}>
              {task.status.replace('_', ' ')}
            </span>
            {task.assignee && (
              <div className="flex items-center gap-1">
                <PersonaAvatar name={task.assignee} size={18} />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{task.assignee}</span>
              </div>
            )}
            {task.priority && (
              <span className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>{task.priority}</span>
            )}
          </div>
        </div>
      </div>

      {/* Status actions */}
      {actions.length > 0 && (
        <div className="flex gap-2 mb-6">
          {actions.map(a => (
            <button
              key={a.next}
              onClick={() => statusMut.mutate(a.next)}
              disabled={statusMut.isPending}
              className="text-xs px-3 py-1.5 rounded-md disabled:opacity-50"
              style={{
                background: a.next === 'completed' ? 'var(--success)' : a.next === 'blocked' ? 'var(--danger)' : 'var(--accent)',
                color: '#fff',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Description */}
      {task.description && (
        <section className="mb-4 p-4 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
          <h2 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Description</h2>
          <MarkdownRenderer content={task.description} />
        </section>
      )}

      {/* Dependencies */}
      {deps.length > 0 && (
        <section className="mb-4 p-4 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
          <h2 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Depends On</h2>
          {deps.map(d => (
            <Link key={d.id} to={`/project/${projectId}/task/${d.id}`} className="flex items-center gap-2 py-1 no-underline">
              <div className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[d.status] }} />
              <span className="text-xs" style={{ color: 'var(--text-primary)' }}>{d.title}</span>
            </Link>
          ))}
        </section>
      )}
      {blockers.length > 0 && (
        <section className="mb-4 p-4 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
          <h2 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Blocks</h2>
          {blockers.map(b => (
            <Link key={b.id} to={`/project/${projectId}/task/${b.id}`} className="flex items-center gap-2 py-1 no-underline">
              <div className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[b.status] }} />
              <span className="text-xs" style={{ color: 'var(--text-primary)' }}>{b.title}</span>
            </Link>
          ))}
        </section>
      )}

      {/* Notes */}
      <section className="mb-4 p-4 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <h2 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Notes</h2>
        {task.notes && <MarkdownRenderer content={task.notes} />}
        {task.completion_notes && (
          <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
            <span className="text-xs font-medium" style={{ color: 'var(--success)' }}>Completion: </span>
            <MarkdownRenderer content={task.completion_notes} />
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={noteInput}
            onChange={e => setNoteInput(e.target.value)}
            placeholder="Add a note..."
            className="flex-1 px-3 py-1.5 rounded text-xs outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            onKeyDown={e => { if (e.key === 'Enter' && noteInput.trim()) submitNote() }}
          />
          <button
            onClick={submitNote}
            disabled={!noteInput.trim()}
            className="text-xs px-3 py-1.5 rounded disabled:opacity-50"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            Add
          </button>
        </div>
      </section>
    </div>
  )
}
